/**
 * @fileoverview สิทธิ์เข้าพอร์ทัลตาม "ประเภทผู้ใช้" (master user_types ที่ backend แนบมากับ profile เป็น user_type_info.portals)
 *
 * ตกลงกับผู้ใช้ 2026-10-13:
 * - พอร์ทัลที่ master เป็น null = เข้าไม่ได้ (การ์ด disable, พิมพ์ URL ตรงเด้ง 403)
 * - โหมด view / ve = "ดูอย่างเดียว" (ปุ่มเพิ่ม/แก้/ลบ หายผ่านกลไก className เดิม)
 *   view = ใช้ได้เฉพาะ permission ที่ลงท้าย .read
 *   ve   = view + export: ใช้ .read และ .export ได้ (ปุ่ม export / download ทั้งหมด) - ตกลง 2026-10-18 (EXE / VWR)
 * - super_user / admin-company เข้าได้ทุกพอร์ทัล
 * - พอร์ทัลมือถือบนเว็บ (/mb) ยังคงเป็นของประเภท "ช่าง" (is_technician) เท่านั้น
 * backend บังคับกติกาเดียวกันที่ checkAuth (ช่องทาง) และ checkPermission (ดูอย่างเดียว)
 */

export const PORTAL_BY_PREFIX = {
  "/ad/": "admin",
  "/wh/": "warehouse",
  "/cs/": "customer",
  "/mb/": "mobile",
  "/su/": "super_user",
};

export const WEB_PORTALS = ["admin", "warehouse", "customer"];
const VIEW_ONLY_MODES = ["view", "ve"];
const VIEW_EXPORT_MODES = ["ve"];
const SYSTEM_TYPES = ["super_user", "admin-company"];

export const readProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("cm-profile") || "null");
  } catch {
    return null;
  }
};

/** พอร์ทัลของ path ปัจจุบัน (null = หน้าที่ไม่อยู่ในพอร์ทัลไหน เช่น /console) */
export const currentPortal = (pathname = window.location.pathname) => {
  const prefix = Object.keys(PORTAL_BY_PREFIX).find((item) => pathname.startsWith(item));
  return prefix ? PORTAL_BY_PREFIX[prefix] : null;
};

export const isSystemType = (profile) => SYSTEM_TYPES.includes(profile?.user_type);

export const isTechnicianProfile = (profile) => profile?.is_technician ?? profile?.user_type === "technician";

/** โหมดพอร์ทัลจาก master (null = backend เก่าที่ยังไม่ส่งมา -> ใช้กติกาเดิม) */
export const portalModes = (profile) => profile?.user_type_info?.portals || null;

/** ประเภทผู้ใช้นี้เข้าพอร์ทัลนี้ได้ไหม */
export const isPortalAllowed = (profile, portal) => {
  if (!profile) return false;
  if (isSystemType(profile)) return true;
  if (portal === "super_user") return false;
  // พอร์ทัลมือถือบนเว็บ = ประเภทช่างเท่านั้น (SUP / EXE ใช้มือถือผ่านแอป ไม่ใช่ผ่านเว็บ)
  if (portal === "mobile") return isTechnicianProfile(profile);

  const modes = portalModes(profile);
  if (!modes) return !isTechnicianProfile(profile); // กติกาเดิม: ช่างห้ามเข้าเว็บ นอกนั้นตาม permission
  return !!modes[portal];
};

/** ดูอย่างเดียวในพอร์ทัลนี้ไหม (view / ve ใน master) */
export const isViewOnly = (profile, portal) => {
  if (!portal || isSystemType(profile)) return false;
  return VIEW_ONLY_MODES.includes(portalModes(profile)?.[portal]);
};

/** ดูอย่างเดียวแต่ใช้ปุ่ม export / download ได้ (ve ใน master) */
export const isViewExport = (profile, portal) => {
  if (!portal || isSystemType(profile)) return false;
  return VIEW_EXPORT_MODES.includes(portalModes(profile)?.[portal]);
};

export const isReadKey = (key) => /\.read$/.test(String(key || ""));
export const isExportKey = (key) => /\.export$/.test(String(key || ""));

/** key นี้ใช้ได้ในพอร์ทัลโหมดนี้ไหม (full = ทุก key, view = .read, ve = .read + .export) */
export const isKeyAllowedInMode = (mode, key) => {
  if (!VIEW_ONLY_MODES.includes(mode)) return true;
  if (isReadKey(key)) return true;
  return VIEW_EXPORT_MODES.includes(mode) && isExportKey(key);
};

/**
 * permission ที่ใช้ได้จริงในพอร์ทัลนี้ - พอร์ทัลดูอย่างเดียวเหลือแค่ .read (ve เหลือ .read + .export)
 * (approve / setting / create ฯลฯ ถือเป็นการกระทำ ถูกตัดออก - ตกลง 2026-10-13, export สำหรับ ve 2026-10-18)
 */
export const effectivePermissions = (profile, portal = currentPortal()) => {
  const perms = profile?.role?.permission || [];
  if (!isViewOnly(profile, portal)) return perms;
  const mode = portalModes(profile)?.[portal];
  return perms.filter((key) => isKeyAllowedInMode(mode, key));
};

/** พอร์ทัลเว็บที่ประเภทนี้เข้าได้ (ไม่รวมมือถือ) */
export const allowedWebPortals = (profile) => WEB_PORTALS.filter((portal) => isPortalAllowed(profile, portal));
